import { useState } from 'react';
import PropTypes from 'prop-types';

import { Link } from 'react-router';

import { connect } from 'react-redux';

import noop from 'lodash/fp/noop';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Typography } from 'Components/Typography';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import { formatPriceWithCurrency } from 'utils/currencies';

import * as trackingActions from 'dux/tracking/actions';

const Product = styled(Link)`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 350px;
  text-align: center;
  color: inherit;
  gap: 34px;
  ${legacyTheme.breakpoints.up('sm')} {
    padding: 0;
  }

  ${legacyTheme.breakpoints.up('md')} {
    max-width: unset;
    margin-bottom: ${legacyTheme.spacing.s24};
  }
`;

const ProductText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing['4x']};

  ${legacyTheme.breakpoints.up('sm')} {
    padding: 0 ${legacyTheme.spacing.s32};
  }

  ${legacyTheme.breakpoints.up('md')} {
    padding: 0 ${legacyTheme.spacing.s80};
  }
`;

const ProductName = styled(Typography)`
  margin-bottom: 8px;
  text-decoration-line: underline;
  text-underline-offset: 5px;
`;

const ImgContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
`;

const Image = styled.img`
  display: block;
  object-fit: cover;
  width: inherit;

  ${legacyTheme.breakpoints.up('sm')} {
    border: 1px solid ${theme.colors.neutral[900]};
    border-right: 0;
  }
`;

const ProductsItem = ({
  content: {
    label,
    currency,
    priceWithSubscription,
    capacity,
    description,
    bgLabelledPicture,
    bgPicture,
    bgPictureAlt,
    slug,
    productPageUrl,
  },
  dataClick,
  dataFrom,
  trackHover,
}) => {
  const [isHovered, setHovered] = useState(false);
  return (
    <Product
      data-click={`${slug}-card`}
      data-from="skincare-grid"
      data-testid={`product-item-card-${slug}`}
      onMouseEnter={() => {
        setHovered(true);
        trackHover({ dataFrom, dataClick, productSlug: slug });
      }}
      onMouseLeave={() => setHovered(false)}
      to={productPageUrl}
    >
      <ImgContainer>
        <Image
          alt={bgPictureAlt}
          src={
            isHovered ? bgLabelledPicture?.src ?? bgLabelledPicture : bgPicture?.src ?? bgPicture
          }
        />
      </ImgContainer>
      <ProductText>
        <div>
          <ProductName markupName="h3" variant="h2">
            Custom {label}
          </ProductName>

          <Typography variant="p1">
            From {formatPriceWithCurrency({ price: priceWithSubscription, currency })} | {capacity}
          </Typography>
        </div>
        <Typography color="grey" variant="p2">
          {description}
        </Typography>
      </ProductText>
    </Product>
  );
};

ProductsItem.propTypes = {
  dataClick: PropTypes.string,
  dataFrom: PropTypes.string,
  trackHover: PropTypes.func,
  content: PropTypes.shape({
    label: PropTypes.string.isRequired,
    pictureLabels: PropTypes.arrayOf(
      PropTypes.shape({
        descriptionBold: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
    priceWithSubscription: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    capacity: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    bgLabelledPicture: srcOrNextImageProp.isRequired,
    bgPicture: srcOrNextImageProp.isRequired,
    bgPictureAlt: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    productPageUrl: PropTypes.string.isRequired,
  }).isRequired,
};

ProductsItem.defaultProps = {
  dataClick: 'see-product',
  dataFrom: 'landing-page',
  trackHover: noop,
};

export default connect(null, {
  trackHover: trackingActions.trackHover,
})(ProductsItem);
